import { ReactNode, useMemo } from 'react';
import { PurchaseOrderLine, NonConformanceReport } from '../../lib/api';
import { ChangelogEntry } from '../common/Changelog';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Package, MessageSquare, HelpCircle } from 'lucide-react';
import POLineGRNTab from './POLineGRNTab';
import POLineASNTab from './POLineASNTab';
import POLinePricebookTab from './POLinePricebookTab';
import ShortageManagement from './ShortageManagement';
import POLineNCRTab from './POLineNCRTab';
import POLineConcessionTab from './POLineConcessionTab';
import CommunicationLog from './CommunicationLog';
import SupplierQueryTab from './SupplierQueryTab';
import ChangeRequestsTab from './ChangeRequestsTab';
import { countChangeRequests } from './changeRequestGrouping';
import { pendingActionCount } from './pendingProposal';

interface POLineDetailTabsProps {
  line: PurchaseOrderLine;
  lineId: number;
  userType: 'customer' | 'supplier';
  activeTab: string;
  onTabChange: (tab: string) => void;
  /** The Detail tab's content, drawn by the modal that owns its formatters. */
  detailContent: ReactNode;
  /** The line's real change log — the Change Requests tab and its count. */
  changelogs: ChangelogEntry[];
  grnCount: number;
  /** Null = the NCRs could not be loaded; the label then carries no count. */
  ncrs: NonConformanceReport[] | null;
  /** CR-6409: null = could not be loaded. */
  asnCount: number | null;
  /** CR-6409: the other side's unread messages; null = could not be loaded. */
  unreadMessageCount: number | null;
  /**
   * Reload the line (and its grid row) after a change in the Change Requests
   * tab, or after a query raised in the Queries tab — a customer's query puts
   * the line on hold.
   */
  onChangeRequestUpdate: () => Promise<void>;
  /** CR-8718: the viewer's request was declined and theirs to resolve (Acknowledge / Amend). */
  mayResolveDecline?: boolean;
  /** CR-8718: Amend Request -- open the modal's Propose Change dialog. */
  onAmendRequest?: () => void;
  /** Reload the line after a shortage is resolved. */
  onShortageUpdate: () => void;
  /** CR-6409: the Communication tab has marked the other side's messages read. */
  onMessagesRead: () => void;
  /** CR-7415: a delivery was changed in the GRN tab's Delivery Details window; reload the line. */
  onDeliveryChanged?: () => void;
}

const TRIGGER_CLASS = 'data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary';

/** CR-6409: the "(n)" every count-bearing tab uses — GRN set the pattern. */
const withCount = (label: string, count: number | null) => (count === null ? label : `${label} (${count})`);

/**
 * The red pill this modal uses for something needing the viewer's attention —
 * the changes waiting on them, and (CR-6409) messages they have not read.
 * Nothing at zero: a pill that is always there stops being read.
 */
function AttentionPill({ count, label }: { count: number | null; label: string }) {
  if (!count) return null;
  return (
    <span
      className="ml-1 min-w-[1.25rem] rounded-full bg-red-500 px-1.5 text-xs font-semibold leading-5 text-white"
      aria-label={`${count} ${label}`}
      title={`${count} ${label}`}
    >
      {count}
    </span>
  );
}

/**
 * The PO Line pop-up's tab strip and panels.
 *
 * Lifted out of POLineDetailModal (CR-6409 touched it, so its 500-line limit
 * applies). CR-6409 also gave every tab that lists records a count: Change
 * Requests, ASN/Shipments, GRN and NCR read "(n)", and Communication carries a
 * red pill of the messages the viewer has not read.
 *
 * @param props - See {@link POLineDetailTabsProps}.
 * @returns The tabs, with the active one's panel.
 */
export default function POLineDetailTabs({
  line, lineId, userType, activeTab, onTabChange, detailContent, changelogs,
  grnCount, ncrs, asnCount, unreadMessageCount,
  onChangeRequestUpdate, mayResolveDecline = false, onAmendRequest, onShortageUpdate, onMessagesRead,
  onDeliveryChanged,
}: POLineDetailTabsProps) {
  const { isOpenOrdersControlHidden, isConcessionEnabledForCustomer } = useMenuSettings();
  // CR-8701: "Hide Communication Box" takes the Communication tab away from
  // everyone on the line's customer site: its customer users and its suppliers
  // alike (CR-5884 hid it from suppliers only), and the server refuses a post.
  // CR-7739: decided by the line's own customer site (session value as fallback).
  const hideCommunicationTab = line.communication_box_hidden
    ?? isOpenOrdersControlHidden('hide_communication_box', true);
  // A caller asking for the hidden tab (a notification deep-link) lands on
  // Detail rather than on a blank panel with no trigger.
  const requestedTab = hideCommunicationTab && activeTab === 'communication' ? 'detail' : activeTab;
  const changeRequestCount = useMemo(() => countChangeRequests(changelogs, line), [changelogs, line]);

  return (
    <Tabs
      // CR-6411: `initialTab` is caller-supplied, so a supplier-side
      // caller could land on the customer-only Pricebooks tab and get a
      // blank content area with no trigger to click back from. Fall
      // back to Detail, same guard shape as ContractPriceDetailModal.
      value={userType !== 'customer' && requestedTab === 'pricebooks' ? 'detail' : requestedTab}
      onValueChange={onTabChange}
      className="w-full"
    >
      <div className="border-b px-4 pt-2">
        <TabsList className="bg-transparent border-none">
          <TabsTrigger value="detail" className={TRIGGER_CLASS}>Detail</TabsTrigger>
          <TabsTrigger value="change-requests" className={TRIGGER_CLASS}>
            <span className="flex items-center gap-1">
              <MessageSquare className="h-4 w-4" />{withCount('Change Requests', changeRequestCount)}
              {/* EXPEDITING-3: how many changes are waiting on this viewer. */}
              <AttentionPill count={pendingActionCount(line, userType)} label="waiting on you" />
            </span>
          </TabsTrigger>
          {/* CR-6411: Pricebooks sits immediately before ASN/Shipments.
              Customer-side only — contract pricing is customer-managed
              commercial data and stays hidden from suppliers (CR-5552);
              the endpoint enforces the same rule server-side. */}
          {userType === 'customer' && (
            <TabsTrigger value="pricebooks" className={TRIGGER_CLASS}>Pricebooks</TabsTrigger>
          )}
          <TabsTrigger value="asn" className={TRIGGER_CLASS}>{withCount('ASN/Shipments', asnCount)}</TabsTrigger>
          {userType === 'customer' && (
            <TabsTrigger value="grn" className={TRIGGER_CLASS}>{withCount('GRN', grnCount)}</TabsTrigger>
          )}
          {/* Customer review 11 Sep (item 30b): count like GRN (n). */}
          <TabsTrigger value="ncr" className={TRIGGER_CLASS}>{withCount('NCR', ncrs ? ncrs.length : null)}</TabsTrigger>
          {/* CR-6510: the drill-down the PO-line concession badge
              implies. Shown only when this line carries a raised
              concession, and only where its customer runs the
              workflow — the same per-line gate as raising one. */}
          {line.concession_status
            && isConcessionEnabledForCustomer(line.customer_id) && (
            <TabsTrigger value="concessions" className={TRIGGER_CLASS}>Concessions</TabsTrigger>
          )}
          {!hideCommunicationTab && (
            <TabsTrigger value="communication" className={TRIGGER_CLASS}>
              <span className="flex items-center gap-1">
                Communication
                {/* CR-6409: unread, not total — what a message count means to
                    anyone who has used a chat. Clears once the tab is opened. */}
                <AttentionPill count={unreadMessageCount} label="unread" />
              </span>
            </TabsTrigger>
          )}
          {/* CR-8681: the supplier-query thread. The query notifications
              (raised / responded / resolved) deep-link here with
              lineTab=queries; with no tab to land on, a raised query was
              visible to neither side. Both sides see it: the supplier raises
              and resolves, the customer acknowledges and answers. */}
          <TabsTrigger value="queries" className={TRIGGER_CLASS}>
            <span className="flex items-center gap-1">
              <HelpCircle className="h-4 w-4" />Queries
            </span>
          </TabsTrigger>
          {userType === 'customer' && line.shortage_qty && (
            <TabsTrigger value="shortage" className={TRIGGER_CLASS}>
              <span className="flex items-center gap-2">
                Shortage
                {!line.shortage_resolved && <span className="bg-red-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded">!</span>}
              </span>
            </TabsTrigger>
          )}
          {userType === 'supplier' && (
            <TabsTrigger value="cfc" className={TRIGGER_CLASS}>CFC</TabsTrigger>
          )}
        </TabsList>
      </div>

      <TabsContent value="detail" className="m-0 p-4">{detailContent}</TabsContent>

      <TabsContent value="grn" className="m-0 p-6">
        {/* CR-7415: the footer's OTIF override / acknowledge / reset rewrites
            the verdicts the tab shows, so its decision is the tab's refresh key. */}
        <POLineGRNTab
          lineId={lineId}
          orderedQty={parseFloat(line.quantity)}
          refreshKey={`${!!line.otif_overridden}|${!!line.otif_acknowledged}`}
          onDeliveryChanged={onDeliveryChanged}
        />
      </TabsContent>
      {userType === 'customer' && (
        <TabsContent value="pricebooks" className="m-0 p-6">
          <POLinePricebookTab lineId={lineId} partNumber={line.part_number} />
        </TabsContent>
      )}
      <TabsContent value="asn" className="m-0 p-6"><POLineASNTab lineId={lineId} /></TabsContent>
      {line.shortage_qty && (
        <TabsContent value="shortage" className="m-0 p-6"><ShortageManagement line={line} onUpdate={onShortageUpdate} /></TabsContent>
      )}
      <TabsContent value="cfc" className="m-0 p-6">
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">CFC (Confirmed Fixture Chart) information will be displayed here</p>
          <p className="text-sm text-muted-foreground mt-2">Delivery confirmation and scheduling</p>
        </div>
      </TabsContent>
      <TabsContent value="ncr" className="m-0 p-6"><POLineNCRTab lineId={lineId} /></TabsContent>
      <TabsContent value="concessions" className="m-0 p-6"><POLineConcessionTab lineId={lineId} /></TabsContent>
      <TabsContent value="change-requests" className="m-0 p-6">
        <ChangeRequestsTab
          line={line}
          changelogs={changelogs}
          userType={userType}
          onUpdate={onChangeRequestUpdate}
          mayResolveDecline={mayResolveDecline}
          onAmendRequest={onAmendRequest}
        />
      </TabsContent>
      {!hideCommunicationTab && (
        <TabsContent value="communication" className="m-0 p-6">
          <CommunicationLog lineId={lineId} poId={line.po} partNumber={line.part_number} onMessagesRead={onMessagesRead} />
        </TabsContent>
      )}
      <TabsContent value="queries" className="m-0 p-6">
        <SupplierQueryTab
          lineId={lineId}
          partNumber={line.part_number}
          batchSerialNo={line.batch_serial_no}
          onLineUpdate={() => { void onChangeRequestUpdate(); }}
        />
      </TabsContent>
    </Tabs>
  );
}
